import {
	buildHistogramTimeline,
	generateFacets,
	generateGeoJson,
	generateGraph,
	generateHierarchicalFacets,
	generateParallelCoordinates,
	generatePropertyHierarchy,
	generateTextClusters,
	HistogramEndpointMajorBin,
	HistogramEndpointMinorBin,
	HistogramEndpointResponse,
	post,
	Result,
	selectPropertyListFromDilByNames,
	values as listValues,
	filter as originalFilter,
	DILProperty, get, put,
} from 'srs-middleware';

import { EndpointRequest } from 'interfaces';

import { throwEmptyResult } from './endpointDecorators';

/**
 * @param result
 * @param dilName
 * @param options
 * @returns {Promise<any>}
 */
export const timeline: EndpointRequest = throwEmptyResult(
	(result: Result, dilName: string, { propertyList, parameters }): Promise<HistogramEndpointResponse> =>
		buildHistogramTimeline(result, selectPropertyListFromDilByNames(dilName, propertyList), parameters)
);

/**
 * @param result
 * @param dil
 * @param propertyList
 * @param parameters
 * @returns {Promise<any>}
 */
export const facets: EndpointRequest = throwEmptyResult((result, dil, { propertyList, parameters }, ignoreParentResult = false) =>
	{
		const {parentId, ...resultWithoutParent} = result; //remove parentId from facets calls
		return generateFacets(ignoreParentResult ? resultWithoutParent : result, selectPropertyListFromDilByNames(dil, propertyList), parameters, dil)
	}
);

/**
 * @param result
 * @param dil
 * @param propertyList
 * @param parameters
 * @returns {Promise<any>}
 */
export const paraCord: EndpointRequest = throwEmptyResult((result, dil, { propertyList, parameters }) =>
	generateParallelCoordinates(result, selectPropertyListFromDilByNames(dil, propertyList), parameters)
);

/**
 * @param result
 * @param dil
 * @param propertyList
 * @param parameters
 * @returns {Promise<any>}
 */
export const textClusters: EndpointRequest = throwEmptyResult((result, dil, { propertyList, parameters }) =>
	generateTextClusters(result, selectPropertyListFromDilByNames(dil, propertyList), { maxClusters: 15, ...parameters })
);

/**
 * @param result
 * @param dil
 * @param propertyList
 * @param parameters
 * @returns {Promise<any>}
 */
export const graph: EndpointRequest = throwEmptyResult(
	(result, dil, { propertyList, parameters: { metadata, ...otherParams } }) => {
		const fullMetadata = selectPropertyListFromDilByNames(dil, metadata || []);
		return generateGraph(result, selectPropertyListFromDilByNames(dil, propertyList), {
			metadata: fullMetadata,
			...otherParams,
		});
	}
);

/**
 * @param result
 * @param dil
 * @param propertyList
 * @param parameters
 * @returns {Promise<any>}
 */
export const nodeGraph: EndpointRequest = throwEmptyResult(
	(result, dil, { propertyList, parameters: { metadata, ...otherParams } }) => {
		const fullMetadata = selectPropertyListFromDilByNames(dil, metadata || []);
		return generateNodeGraph(result, selectPropertyListFromDilByNames(dil, propertyList), {
			metadata: fullMetadata,
			...otherParams,
		});
	}
);

/**
 * @param result
 * @param dil
 * @param propertyList
 * @param parameters
 * @returns {Promise<any>}
 */
export const clusterGraph: EndpointRequest = throwEmptyResult(
	(result, dil, { propertyList, parameters: { metadata, ...otherParams } }) => {
		const fullMetadata = selectPropertyListFromDilByNames(dil, metadata || []);
		return generateClusterGraph(result, selectPropertyListFromDilByNames(dil, propertyList), {
			metadata: fullMetadata,
			...otherParams,
		});
	}
);

export const geopoints = throwEmptyResult(
	(
		result: Result,
		dilName: string,
		options: {
			propertyList: string[];
			parameters: GeoJsonParams;
		}
	) => {
		return generateGeoJson(result, dilName, options.propertyList, options.parameters);
	}
);

///todo - pull from middleware
interface GeoJsonParams {
	metadataPropertyNames?: string[];
	includeDocumentIds?: boolean;
	enableCaching?: boolean;
}

/**
 * @param result
 * @param dil
 * @param propertyList
 * @param parameters
 * @returns {Promise<any>}
 */
export const propertyHierarchy: EndpointRequest = throwEmptyResult(
	(
		result,
		dil,
		{ propertyList, parameters: { maxRoot, metadata, aggregationProperty, distinctProperty, ...otherParams } }
	) => {
		const filteredProperties = propertyList.filter((x) => !!x);
		const fullMetadata = selectPropertyListFromDilByNames(dil, metadata || []);

		return generatePropertyHierarchy(result, handlePropertySelection(dil, filteredProperties), {
			...otherParams,
			maxRoot: maxRoot,
			metadata: fullMetadata,
			aggregationProperty: aggregationProperty
				? selectPropertyListFromDilByNames(dil, [aggregationProperty])?.[0]
				: null,
			distinctProperty: distinctProperty ? selectPropertyListFromDilByNames(dil, [distinctProperty])?.[0] : null,
		});
	}
);

/**
 * @param result
 * @param dil
 * @param propertyList
 * @param parameters
 * @returns {Promise<any>}
 */
export const matrix: EndpointRequest = throwEmptyResult(
	(result, dil, { propertyList, parameters: { aggregationProperty, ...otherParams } }) => {
		//return generateMatrix(result, selectPropertyListFromDilByNames(dil, propertyList), parameters);
		return generateMatrix(result, selectPropertyListFromDilByNames(dil, propertyList), {
			...otherParams,
			aggregationProperty: aggregationProperty
				? selectPropertyListFromDilByNames(dil, [aggregationProperty])?.[0]
				: null,
		});
	}
);

/**
 * @param result
 * @param dil
 * @param propertyList
 * @param parameters
 * @returns {Promise<any>}
 */
export const hierarchicalFacets: EndpointRequest = throwEmptyResult((result, dil, { propertyList, parameters }) =>
	generateHierarchicalFacets(result, dil, propertyList, parameters)
);

// TODO: update path to use generatePathHierarchy once the middleware has been updated to use MaxChildren
/**
 * @param result
 * @param dil
 * @param propertyList
 * @param parameters
 * @returns {Promise<any>}
 */
export const path: EndpointRequest = throwEmptyResult((result, dil, { propertyList, parameters }) =>
	post(`/SRS/Analytics/Categorical/${result.resultId}/PathHierarchy`, {
		property: selectPropertyListFromDilByNames(dil, propertyList)[0],
		parameters: {
			...parameters,
		},
	})
);

// TODO: Why is this so different looking
/**
 * @param result
 * @param dil
 * @param propertyList
 * @returns {Promise<any>}
 */
export const values: EndpointRequest = throwEmptyResult((
	result,
	dil,
	{ columns, parameters: { MaxValues = null, sortProperty = "", ...otherParams } = {} }
) => {
	const startEndProps =
		MaxValues != null
			? {
					startIndex: 0,
					endIndex: MaxValues - 1,
			  }
			: {};
	const fullSortProperty = selectPropertyListFromDilByNames(dil, [sortProperty])?.[0];
	return listValues(result, selectPropertyListFromDilByNames(dil, columns), {
		...startEndProps,
		sortProperty: fullSortProperty,
		...otherParams,
	});
});

// TODO: Not sure of the use case for supporting multiple of the same property type,
//  if so we should support directly in the to the middleware call with a boolean flag or something
const handlePropertySelection = (dil, properties) => {
	// Allows for multiple of the same property to be returned from selectPropertyListFromDilByNames
	const duplicateProperties = [];
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		if (properties.indexOf(element) !== properties.lastIndexOf(element)) {
			duplicateProperties.push(properties.pop(index));
		}
	}
	return selectPropertyListFromDilByNames(dil, properties).concat(
		selectPropertyListFromDilByNames(dil, duplicateProperties)
	);
};

/* EDITED SRS-MIDDLEWARE ENDPOINTS */
/**
 * When serberus 2.0 comes out, this functionality should
 * be moved up-stream to srs-middleware.
 *
 * The purpose of these edited endpoints are to catch endpoint requests
 * made with empty results. This makes handling this error case much easier
 */
export const filter = throwEmptyResult(originalFilter);

/*  NEW SRS-MIDDLEWARE ENDPOINTS */

// These all look the same so I'm abstracting it for my new endpoints.
// Once I figure out if I'd be stepping on any feet I may change the old
// endpoints to use this as well.
type PathFunc = (resultId:number)=>string
/**
 * A function for making a new endpoint function
 * @param pathFunc A function that will create the correct post path from a
 * result id.
 * @param singleProperty Whether or not to only use the first property in the
 * properties object.
 * @returns an endpoint function
 */
const makeEndpoint = <EndReturn>(pathFunc:PathFunc,singleProperty:boolean=false):
		((result:Result | number,properties:DILProperty[],parameters:{})=>Promise<EndReturn>) => {
	return throwEmptyResult(async (result, properties, parameters = {}) => {
		const resultId = typeof result === 'number' ? result : result.resultId;
		
		const postPropertyData = properties.length===0 ? {} :
			singleProperty ? 
				{property: properties.map(
					({ systemDefinitionId, name }) => 
						({ systemDefinitionId, name }))[0]} 
				:
				{propertyList: properties.map(
					({ systemDefinitionId, name }) => 
						({ systemDefinitionId, name }))}

		const postData = {...postPropertyData,parameters}

		return post(pathFunc(resultId), postData);
	});
}

const makeEndpointWithDil= <EndReturn>(pathFunc:PathFunc,singleProperty:boolean=false):
		((result:Result | number, dilName:string, { propertyList, parameters })=>Promise<EndReturn>) => {
			
	const endPointFunc = makeEndpoint<EndReturn>(pathFunc,singleProperty)
	return (result, dilName, { propertyList, parameters }) => {
		const properties = selectPropertyListFromDilByNames(dilName, propertyList);
		return endPointFunc(result,properties,parameters)
	}
}

// If you need to create a new endpoint, add it here, document the need to move in a Jira ticket and then it will be moved to middleware in another PR
// TODO: MOVE TO SRS-MIDDLEWARE
const generateMatrix = throwEmptyResult(async (result, properties, parameters = {}) => {
	const resultId = typeof result === 'number' ? result : result.resultId;

	return post(`/SRS/Analytics/Categorical/${resultId}/Matrix`, {
		propertyList: properties.map(({ systemDefinitionId, name }) => ({ systemDefinitionId, name })),
		parameters,
	});
});

const generateNodeGraph = throwEmptyResult(async (result, properties, parameters = {}) => {
	const resultId = typeof result === 'number' ? result : result.resultId;

	return post(`/SRS/Analytics/Categorical/${resultId}/NodeGraph`, {
		propertyList: properties.map(({ systemDefinitionId, name }) => ({ systemDefinitionId, name })),
		parameters,
	});
});

//TODO: move to srs middleware
export const choropleth = throwEmptyResult(
	async (
		result: Result,
		dilName: string,
		options: {
			propertyList: string[];
			parameters: GeoJsonParams;
		}
	) => {
		const resultId = typeof result === 'number' ? result : result.resultId;
		return post(`/SRS/Analytics/Geospatial/${resultId}/Choropleth`, {
			propertyList: handlePropertySelection(dilName, options?.parameters?.metadataPropertyNames ?? []),
			parameters: options?.parameters ?? {
				enableCaching: false,
			},
		});
	}
);

const generateClusterGraph = throwEmptyResult(async (result, properties, parameters = {}) => {
	const resultId = typeof result === 'number' ? result : result.resultId;

	return post(`/SRS/Analytics/Categorical/${resultId}/ClusterGraph`, {
		propertyList: properties.map(({ systemDefinitionId, name }) => ({ systemDefinitionId, name })),
		parameters,
	});
});

export const facetsRatio = throwEmptyResult(async (result, dil, { propertyList, parameters }) => {
	const resultId = typeof result === 'number' ? result : result.resultId;
	const properties = selectPropertyListFromDilByNames(dil, propertyList);
	return post(`/SRS/Analytics/Categorical/${resultId}/FacetsRatio`, {
		property: properties.map(({ systemDefinitionId, name }) => ({ systemDefinitionId, name }))[0],
		parameters,
	});
});

export const facetsHierarchy = throwEmptyResult(async (result, dil, { propertyList, parameters }) => {
	const resultId = typeof result === 'number' ? result : result.resultId;
	const properties = selectPropertyListFromDilByNames(dil, propertyList);
	return post(`/SRS/Analytics/Categorical/${resultId}/FacetsHierarchy`, {
		property: properties.map(({ systemDefinitionId, name }) => ({ systemDefinitionId, name }))[0],
		parameters,
	});
});

export const getCuratedViewsHighlights = throwEmptyResult(async () => {
	return get(`/SRS/Application/NCATS/CuratedViews`);
});

export const logCuratedViewVisit = throwEmptyResult(async (payload) => {
	return put(`/SRS/Application/NCATS/CuratedView`, payload);
});

export const updateCuratedViewFavStatus = throwEmptyResult(async (payload: {id: string, favorite: boolean}) => {
	return put(`/SRS/Application/NCATS/CuratedView/Favorite`, payload);
});


export const duration = makeEndpointWithDil(
	(resultId)=>`/SRS/Analytics/Temporal/${resultId}/Duration`
)
export const interval = makeEndpointWithDil(
	(resultId)=>`/SRS/Analytics/Temporal/${resultId}/Interval`
)

export const similarOrgs = (result,parameters) => 
	makeEndpoint(
		(resultId)=>`/SRS/Application/NCATS/${resultId}/OrganizationSimilarity`
	)(result,[],parameters) // This endpoint doesn't take properties so neither does the function

export const orgPerformance = (result,parameters) => 
	makeEndpoint(
		(resultId)=>`/SRS/Application/NCATS/${resultId}/OrganizationPerformance`
	)(result,[],parameters) // This endpoint doesn't take properties so neither does the function

export const coverage = makeEndpointWithDil(
	(resultId)=>`/SRS/Application/NCATS/${resultId}/Coverage`
)

export type StackedHistogramEndpointMinorBin = {
	counts: Record<string, number>;
} & Omit<HistogramEndpointMinorBin, 'count'>;

export type StackedHistogramEndpointMajorBin = {
	binData: StackedHistogramEndpointMinorBin[];
} & Omit<HistogramEndpointMajorBin, 'binData'>;

export type StackedHistogramEndpointResponse = {
	bins: StackedHistogramEndpointMajorBin[];
	keys: string[];
} & Omit<HistogramEndpointResponse, 'bins'>;

/**
 * Distinguishes between Stacked and normal HistogramEndpointResponses
 */
export function isStacked(arg): arg is StackedHistogramEndpointResponse {
	return typeof arg.keys !== undefined
}

/**
 * @param result
 * @param dilName
 * @param options
 * @returns {Promise<any>}
 */
export const stackedTimeline: EndpointRequest = throwEmptyResult(
	(result: Result, dilName: string, { propertyList, parameters }): Promise<StackedHistogramEndpointResponse> => {
		const resultId = typeof result === 'number' ? result : result.resultId;
		const properties = selectPropertyListFromDilByNames(dilName, propertyList);
		const stackProperty = selectPropertyListFromDilByNames(dilName, parameters.stackProperty)[0];

		return post<StackedHistogramEndpointResponse>(`/SRS/Analytics/Temporal/${resultId}/StackedTimeline`, {
			propertyList: properties.map(({ systemDefinitionId, name }) => ({ systemDefinitionId, name })),
			parameters: {
				...parameters,
				stackProperty: { ...stackProperty },
			},
		});
	}
);
